import {useQuery} from '@apollo/client'
import React, {useEffect} from 'react'
import {GET_LEAD_LOGS} from '../../../gql/queries/leadsQuery'
import {toast} from 'react-toastify'
import {Accordion, Spinner} from 'react-bootstrap'
import moment from 'moment'

interface ILeadActivityProps {
  id: string
}

interface IResLogsLead {
  leadLogsByLeadId: {
    id: string
    oldValue: any
    newValue: any
    updatedAt: string
    user: {
      id: string
      firstName: string
      lastName: string
    }
    changes: string[]
  }[]
}

const toDict = (val: any) => {
  if (val == null) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch (e) {
      return val
    }
  }
  if (typeof val === 'object') return val
  return {}
}

const isPlainMessage = (oldVal: any, newVal: any) => {
  return (typeof oldVal === 'string' && oldVal.trim() !== '') || (typeof newVal === 'string' && newVal.trim() !== '')
}

const humanizeKey = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return <pre className='mb-0'>{JSON.stringify(value, null, 2)}</pre>
  return String(value)
}

export const LeadActivityTab: React.FC<ILeadActivityProps> = ({id}) => {
  const {data, loading, error} = useQuery<IResLogsLead>(GET_LEAD_LOGS, {
    variables: {leadId: Number(id)},
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  if (loading || !data) {
    return (
      <div className='d-flex flex-center' style={{height: '20vh'}}>
        <Spinner animation='border' />
      </div>
    )
  }

  if (data.leadLogsByLeadId.length === 0) {
    return (
      <div className='d-flex flex-center' style={{height: '20vh'}}>
        <h4 className='text-muted'>There is no activity recorded at the moment</h4>
      </div>
    )
  }

  const logs = [...data.leadLogsByLeadId].reverse()
  const total = logs.length

  return (
    <Accordion defaultActiveKey={`${total}`}>
      {logs.map((log, index) => {
        const {oldValue, newValue, updatedAt, user, changes} = log
        const oldParsed = toDict(oldValue)
        const newParsed = toDict(newValue)
        const number = total - index

        let differences: {field: string; oldValue: any; newValue: any}[] = []

        if (!isPlainMessage(oldParsed, newParsed)) {
          const oldObj = typeof oldParsed === 'object' && oldParsed ? (oldParsed as Record<string, any>) : {}
          const newObj = typeof newParsed === 'object' && newParsed ? (newParsed as Record<string, any>) : {}
          const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
          allKeys.forEach((key) => {
            if (key === 'updated') return
            const prev = oldObj[key]
            const next = newObj[key]
            if (JSON.stringify(prev) !== JSON.stringify(next)) {
              differences.push({field: key, oldValue: prev, newValue: next})
            }
          })
        }

        return (
          <Accordion.Item eventKey={`${number}`} key={log.id} className='log-entry'>
            <Accordion.Header className='fw-bold'>
              <span>{number}:</span>&nbsp;
              <span>{moment(updatedAt).format('YYYY-MM-DD HH:mm:ss')}</span>&nbsp;-&nbsp;
              <span className='fw-bold'>
                {user.firstName} {user.lastName}
              </span>
            </Accordion.Header>
            <Accordion.Body>
              <p>
                <strong>Changes:</strong>{' '}
                {Array.isArray(changes) && changes.length > 0 ? changes.join(', ') : '—'}
              </p>
              {isPlainMessage(oldParsed, newParsed) ? (
                <div className='text-muted'>No structured changes to display.</div>
              ) : (
                <table className='table table-striped'>
                  <thead>
                    <tr>
                      <th className='fw-bold'>Field</th>
                      <th className='fw-bold'>Previous Value</th>
                      <th className='fw-bold'>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {differences.length > 0 ? (
                      differences.map(({field, oldValue, newValue}, i) => (
                        <tr key={i}>
                          <td>{humanizeKey(field)}</td>
                          <td>{renderValue(oldValue)}</td>
                          <td>{renderValue(newValue)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3}>There are no differences</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </Accordion.Body>
          </Accordion.Item>
        )
      })}
    </Accordion>
  )
}
